import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, api } from './api';
import { IS_IOS, IS_IOS_NON_SAFARI } from './platform';

/**
 * Web Speech API (`SpeechRecognition`) 미지원 브라우저용 폴백.
 * MediaRecorder 로 마이크 녹음 → blob → POST /api/stt/transcribe → 텍스트.
 *
 * 반환 시그니처는 `useSpeechRecognition` 과 동일해서 ChatComposer 가 hook 만 바꿔 끼울 수 있다.
 * 단 `interimTranscript` 는 항상 빈 문자열 (서버는 streaming partial 안 함).
 */

export interface UseServerSpeechRecognitionOptions {
  lang?: string;
  /** 안전 자동 stop (ms). 기본 30000 (Gemma 4 audio 한도 + 백엔드 부하 보호). */
  maxDurationMs?: number;
  /** false 면 hook 비활성 — supported=false 로 보고. */
  enabled?: boolean;
}

export interface UseServerSpeechRecognitionReturn {
  supported: boolean;
  listening: boolean;
  finalTranscript: string;
  interimTranscript: string;
  error: string | null;
  start(): void;
  stop(): void;
  reset(): void;
}

function detectMediaSupport(): boolean {
  if (typeof window === 'undefined') return false;
  if (typeof navigator === 'undefined') return false;
  if (!navigator.mediaDevices?.getUserMedia) return false;
  if (typeof MediaRecorder === 'undefined') return false;
  return true;
}

/** MediaRecorder 가 받아주는 첫 mime 선택. webm/opus 우선. */
function pickMimeType(): string {
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/mp4', // Safari
  ];
  for (const m of candidates) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported?.(m)) return m;
  }
  return ''; // 빈 문자열 → 브라우저 default
}

export function useServerSpeechRecognition(
  options: UseServerSpeechRecognitionOptions = {},
): UseServerSpeechRecognitionReturn {
  const { lang = 'ko-KR', maxDurationMs = 30_000, enabled = true } = options;

  const baseSupported = detectMediaSupport();
  const supported = enabled && baseSupported;

  const [listening, setListening] = useState(false);
  const [finalTranscript, setFinalTranscript] = useState('');
  const [error, setError] = useState<string | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const safetyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelledRef = useRef(false);

  const cleanupStream = () => {
    if (streamRef.current) {
      for (const t of streamRef.current.getTracks()) t.stop();
      streamRef.current = null;
    }
    recorderRef.current = null;
  };

  const clearSafetyTimer = () => {
    if (safetyTimerRef.current) {
      clearTimeout(safetyTimerRef.current);
      safetyTimerRef.current = null;
    }
  };

  const reset = useCallback(() => {
    setFinalTranscript('');
    setError(null);
  }, []);

  const stop = useCallback(() => {
    clearSafetyTimer();
    const r = recorderRef.current;
    if (r && r.state !== 'inactive') {
      try {
        r.stop();
      } catch {
        // ignore — 이미 종료
      }
    }
  }, []);

  const start = useCallback(async () => {
    if (!supported) {
      setError('이 브라우저는 마이크 녹음을 지원하지 않습니다.');
      return;
    }
    setError(null);
    setFinalTranscript('');
    cancelledRef.current = false;
    chunksRef.current = [];

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      // err.name 이 표준 — DOMException 의 name 으로 분기.
      const name = err instanceof Error ? err.name : '';
      const msg = err instanceof Error ? err.message : '';
      const iosSuffix = IS_IOS_NON_SAFARI
        ? ' (iOS 의 Chrome / Firefox 는 마이크 접근이 제한될 수 있어요. Safari 에서 시도해 보세요.)'
        : IS_IOS
          ? ' (설정 → Safari → 마이크 에서 권한을 다시 확인해 주세요.)'
          : '';
      if (name === 'NotAllowedError' || /NotAllowed|Permission/i.test(msg)) {
        setError(`마이크 권한이 거부되었습니다.${iosSuffix}`);
      } else if (name === 'NotFoundError' || /NotFound/.test(msg)) {
        setError('마이크를 찾을 수 없습니다.');
      } else if (name === 'NotReadableError') {
        setError('다른 앱이 마이크를 사용 중입니다.');
      } else if (name === 'SecurityError' || /SecurityError/.test(msg)) {
        setError('보안 컨텍스트가 아닙니다 (HTTPS 필요).');
      } else {
        setError(`마이크 접근 실패: ${name || msg || 'unknown'}${iosSuffix}`);
      }
      return;
    }
    streamRef.current = stream;

    const mimeType = pickMimeType();
    let recorder: MediaRecorder;
    try {
      recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
    } catch (err) {
      cleanupStream();
      setError(`녹음기 초기화 실패: ${err instanceof Error ? err.message : 'unknown'}`);
      return;
    }
    recorderRef.current = recorder;

    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };

    recorder.onstop = async () => {
      clearSafetyTimer();
      const stream = streamRef.current;
      cleanupStream();
      setListening(false);

      if (cancelledRef.current) return;
      const blob = new Blob(chunksRef.current, {
        type: chunksRef.current[0]?.type || 'audio/webm',
      });
      chunksRef.current = [];
      if (blob.size === 0) {
        setError('녹음된 오디오가 없습니다.');
        return;
      }
      try {
        const res = await api.sttTranscribe(blob, lang);
        setFinalTranscript(res.text);
      } catch (err) {
        if (err instanceof ApiError) {
          setError(err.message);
        } else {
          setError(err instanceof Error ? err.message : '서버 인식 실패.');
        }
      }
      // suppress unused warning
      void stream;
    };

    recorder.onerror = () => {
      setError('녹음 중 오류가 발생했습니다.');
      cleanupStream();
      setListening(false);
    };

    try {
      recorder.start();
      setListening(true);
      if (maxDurationMs > 0) {
        safetyTimerRef.current = setTimeout(() => {
          try {
            if (recorder.state !== 'inactive') recorder.stop();
          } catch {
            // ignore
          }
        }, maxDurationMs);
      }
    } catch (err) {
      cleanupStream();
      setError(`녹음 시작 실패: ${err instanceof Error ? err.message : 'unknown'}`);
    }
  }, [supported, lang, maxDurationMs]);

  // unmount 시 정리.
  useEffect(() => {
    return () => {
      cancelledRef.current = true;
      clearSafetyTimer();
      const r = recorderRef.current;
      if (r && r.state !== 'inactive') {
        try {
          r.stop();
        } catch {
          // ignore
        }
      }
      cleanupStream();
    };
  }, []);

  return {
    supported,
    listening,
    finalTranscript,
    interimTranscript: '',
    error,
    start,
    stop,
    reset,
  };
}
